import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/shared/lib/cn';

const buttonVariants = cva(
  // Apple parity for disabled state: not a faded-tint of the enabled colour
  // but a flat, opaque neutral gray with WHITE text — exactly mirrors the
  // Apple `ProminentButtonStyle` (systemGray3 fill `--button-disabled`,
  // `.foregroundStyle(.white)`). Applied globally on the base so every
  // variant — default/destructive/teal/outline/etc. — collapses to the same
  // gray-with-white-text look when disabled, without each variant opting in.
  // `disabled:opacity-100` cancels the legacy `opacity-50` fade;
  // `disabled:shadow-none` and `disabled:hover:scale-100` strip the hover
  // affordance so the button reads as inert.
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full text-form-label transition-all duration-150 disabled:pointer-events-none disabled:bg-button-disabled disabled:text-white disabled:opacity-100 disabled:shadow-none disabled:hover:scale-100 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default:
          'bg-primary text-primary-foreground shadow-xs hover:bg-primary/90 hover:scale-[1.02] hover:shadow-md active:scale-[0.98]',
        destructive:
          'bg-destructive text-destructive-foreground shadow-xs hover:bg-destructive/90 hover:scale-[1.02] hover:shadow-md active:scale-[0.98] focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60',
        // Soft borderless gray fill (Apple-like). Replaced the legacy
        // bordered look — minimizing chrome borders sitewide.
        outline:
          'bg-muted text-foreground hover:bg-muted/70 hover:scale-[1.02] active:scale-[0.98]',
        secondary:
          'bg-muted text-foreground hover:bg-muted/70 hover:scale-[1.02] active:scale-[0.98]',
        ghost:
          'bg-button-simple text-button-simple-foreground border border-button-simple-border hover:bg-button-simple-hover hover:text-button-simple-hover-foreground hover:border-button-simple-hover-border hover:scale-[1.02] active:bg-button-simple-active active:text-button-simple-active-foreground active:scale-[0.98]',
        link: 'text-primary underline-offset-4 hover:underline',
        teal: 'bg-primary text-primary-foreground shadow-xs hover:bg-primary/90 hover:scale-[1.02] hover:shadow-md active:scale-[0.98]',
      },
      size: {
        default: 'h-9 px-4 py-2 has-[>svg]:px-3',
        sm: 'h-8 rounded-full gap-1.5 px-3 has-[>svg]:px-2.5',
        lg: 'h-10 rounded-full px-6 has-[>svg]:px-4',
        icon: 'size-9',
        // Canonical primary call-to-action capsule — every Generate /
        // Regenerate button across the concept tabs (Views / Estimate /
        // Blueprint) MUST use this so they stay visually identical and can be
        // retuned in ONE place. Apple `.accentCapsule` parity: compact 40pt
        // (h-10) pill that HUGS its label (inline-flex base → no w-full /
        // flex-1) and is centered by its container; never a full-width bar.
        cta: 'h-10 rounded-full px-6 has-[>svg]:px-5',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    Omit<VariantProps<typeof buttonVariants>, 'variant' | 'size'> {
  variant?:
    | 'default'
    | 'destructive'
    | 'outline'
    | 'secondary'
    | 'ghost'
    | 'link'
    | 'teal'
    | 'primary';
  size?: 'default' | 'sm' | 'lg' | 'icon' | 'md' | 'cta';
  asChild?: boolean;
  loading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      loading = false,
      disabled,
      children,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : 'button';

    // Map legacy variants to new ones
    const mappedVariant = variant === 'primary' ? 'default' : variant;
    const mappedSize = size === 'md' ? 'default' : size;

    return (
      <Comp
        ref={ref}
        disabled={disabled || loading}
        className={cn(
          buttonVariants({
            variant: mappedVariant,
            size: mappedSize,
            className,
          })
        )}
        {...props}
      >
        {loading ? (
          <span className="flex items-center">
            <svg
              className="animate-spin -ml-1 mr-3 h-5 w-5"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            Loading...
          </span>
        ) : (
          children
        )}
      </Comp>
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
